import { object, string } from "prop-types";
import { useLayoutEffect, useMemo, useState } from "react";

import styled from "styled-components/macro";

import { colors } from "UI/colors";

/**
 * Absolute overlay positioned against `anchorRef` (the Street input). Renders
 * the user's typed prefix invisibly so the ghost suffix aligns to it
 * glyph-for-glyph. Measures on every render to follow font/padding changes
 * across input variants. The ghost runs the input's full width; the inline
 * unit-fill affordance overlays it without clipping (see ShadowField).
 */
export default function ShadowText({ anchorRef, typed, suffix }) {
  const [style, setStyle] = useState(null);

  // Track focus on the anchored input so the ghost can dim toward black when
  // the field isn't focused (a focused field keeps the lighter gray). Listen
  // on `document` for the bubbling focusin/focusout rather than focus/blur on
  // the input itself: the input ref isn't populated when this effect first
  // runs (ShadowText renders before its sibling input mounts), and `anchorRef`
  // is a stable ref object so the effect can't re-run to attach late. Document
  // listeners sidestep that — they exist from mount and read the live ref on
  // every focus change.
  const [isFocused, setIsFocused] = useState(false);

  useLayoutEffect(() => {
    const isInputFocused = () => {
      const input = anchorRef && anchorRef.current;
      return Boolean(input) && document.activeElement === input;
    };
    const sync = () => setIsFocused(isInputFocused());
    sync();
    document.addEventListener("focusin", sync);
    document.addEventListener("focusout", sync);
    return () => {
      document.removeEventListener("focusin", sync);
      document.removeEventListener("focusout", sync);
    };
  }, [anchorRef]);

  useLayoutEffect(() => {
    const input = anchorRef && anchorRef.current;
    if (!input || !suffix) {
      setStyle(null);
      return;
    }
    const computedStyle = window.getComputedStyle(input);
    // Measure with fractional getBoundingClientRect rather than the
    // integer-rounded offsetTop/Left/Width/Height. The input commonly sits
    // on sub-pixel boundaries (a 12px label at line-height:normal is ~16.3px
    // tall, so the input's top is fractional); rounding that and then
    // flex-centering the ghost inside a rounded box is what bumps the ghost
    // off the input's text by a pixel or two. The overlay is positioned
    // against the input's offsetParent, so measure top/left relative to that
    // same parent's content box (its border edge + border width).
    const parent = input.offsetParent || input.parentElement;
    const inputRect = input.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(parent);
    // jsdom (and any environment that doesn't compute border widths) returns ""
    // for these, so parseFloat → NaN; fall back to 0 so the overlay's top/left
    // never resolve to NaN (which React rejects as an invalid CSS value).
    const parentBorderTop = parseFloat(parentStyle.borderTopWidth) || 0;
    const parentBorderLeft = parseFloat(parentStyle.borderLeftWidth) || 0;
    const nextStyle = {
      top: inputRect.top - parentRect.top - parentBorderTop,
      left: inputRect.left - parentRect.left - parentBorderLeft,
      width: inputRect.width,
      height: inputRect.height,
      paddingTop: computedStyle.paddingTop,
      paddingRight: computedStyle.paddingRight,
      paddingBottom: computedStyle.paddingBottom,
      paddingLeft: computedStyle.paddingLeft,
      fontFamily: computedStyle.fontFamily,
      fontSize: computedStyle.fontSize,
      fontWeight: computedStyle.fontWeight,
      lineHeight: computedStyle.lineHeight,
      letterSpacing: computedStyle.letterSpacing,
      borderTopWidth: computedStyle.borderTopWidth,
      borderLeftWidth: computedStyle.borderLeftWidth,
    };
    setStyle((prevStyle) => {
      if (
        prevStyle &&
        Object.keys(nextStyle).every((k) => prevStyle[k] === nextStyle[k])
      ) {
        return prevStyle;
      }
      return nextStyle;
    });
  }, [anchorRef, suffix, typed]);

  const overlayStyle = useMemo(
    () =>
      style
        ? {
            top: style.top,
            left: style.left,
            width: style.width,
            height: style.height,
          }
        : null,
    [style]
  );

  const innerStyle = useMemo(
    () =>
      style
        ? {
            paddingTop: style.paddingTop,
            paddingRight: style.paddingRight,
            paddingBottom: style.paddingBottom,
            paddingLeft: `calc(${style.paddingLeft} + ${style.borderLeftWidth})`,
            fontFamily: style.fontFamily,
            fontSize: style.fontSize,
            fontWeight: style.fontWeight,
            lineHeight: style.lineHeight,
            letterSpacing: style.letterSpacing,
          }
        : null,
    [style]
  );

  if (!suffix || !style) return null;

  return (
    <Overlay aria-hidden="true" style={overlayStyle}>
      <Inner style={innerStyle}>
        <Hidden>{typed}</Hidden>
        <Ghost $focused={isFocused}>{suffix}</Ghost>
      </Inner>
    </Overlay>
  );
}

ShadowText.propTypes = {
  anchorRef: object,
  typed: string,
  suffix: string,
};

const Overlay = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  white-space: pre;
  box-sizing: border-box;
`;

const Hidden = styled.span`
  visibility: hidden;
`;

// Focused: the lighter gray it has always been. Unfocused: dim toward black
// (a darker palette gray) so a resting ghost on an inactive field reads as
// more deliberate, less like faint placeholder noise.
const Ghost = styled.span`
  color: ${({ $focused }) => ($focused ? colors.fnGray400 : colors.fnGray700)};
  opacity: 0.75;
`;
